import { registerPlugin, Capacitor } from "@capacitor/core";
import type { PluginListenerHandle } from "@capacitor/core";

/**
 * Bridge to the native IcyRadio ExoPlayer plugin (android/app/.../
 * IcyRadioPlugin.java). Only meaningful on the native Android build; on
 * web `isNativeRadio()` is false and callers fall back to <audio>.
 */

export type IcyState =
  | "idle" | "buffering" | "playing" | "paused" | "ended" | "error";

export interface IcyRadioPlugin {
  play(options: { url: string }): Promise<void>;
  pause(): Promise<void>;
  resume(): Promise<void>;
  stop(): Promise<void>;
  addListener(
    event: "metadata",
    cb: (data: { title: string }) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    event: "state",
    cb: (data: { state: IcyState; error?: string }) => void,
  ): Promise<PluginListenerHandle>;
}

export const IcyRadio = registerPlugin<IcyRadioPlugin>("IcyRadio");

/** True when the native ExoPlayer plugin is present (Android app build). */
export function isNativeRadio(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable("IcyRadio");
}

/**
 * Split a raw ICY StreamTitle into { artist, title }. DR's convention is
 * "Title / Artist"; some streams use "Artist - Title". Mirrors the
 * backend's parseStreamTitle so native + web behave identically.
 */
export function parseIcyTitle(raw: string): { artist: string | null; title: string | null } {
  const s = (raw || "").trim();
  if (!s) return { artist: null, title: null };
  const slash = s.match(/^(.+?)\s+\/\s+(.+)$/);
  if (slash) {
    return { title: slash[1].replace(/\s*-\s*$/, "").trim(), artist: slash[2].trim() };
  }
  const dash = s.match(/^(.+?)\s+[-–]\s+(.+)$/);
  if (dash) return { artist: dash[1].trim(), title: dash[2].trim() };
  return { artist: null, title: s };
}
